import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface EffortScore {
  id: string
  touchpoint: string
  ces_score: number
  responses: number
  trend: string
  improvement_suggestions: string[]
}

interface FrictionPoint {
  id: string
  name: string
  category: string
  effort_level: string
  impact: number
  occurrences: number
  resolution_status: string
}

interface Journey {
  id: string
  name: string
  total_effort: number
  steps: number
  avg_time_minutes: number
  completion_rate: number
}

interface Statistics {
  overall_ces: number
  friction_points: number
  avg_resolution_time: number
  improvement_rate: number
}

export default function CustomerEffortPage() {
  const [scores, setScores] = useState<EffortScore[]>([])
  const [frictionPoints, setFrictionPoints] = useState<FrictionPoint[]>([])
  const [journeys, setJourneys] = useState<Journey[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [scoresRes, frictionRes, journeysRes, statsRes] = await Promise.all([
        fetch('/api/customereffort/scores'),
        fetch('/api/customereffort/friction'),
        fetch('/api/customereffort/journeys'),
        fetch('/api/customereffort/statistics')
      ])
      if (scoresRes.ok) setScores(await scoresRes.json())
      if (frictionRes.ok) setFrictionPoints(await frictionRes.json())
      if (journeysRes.ok) setJourneys(await journeysRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching customer effort data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCESColor = (score: number) => {
    if (score <= 2) return 'text-green-600'
    if (score <= 4) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getEffortBadge = (level: string) => {
    if (level === 'high') return 'bg-red-100 text-red-800'
    if (level === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'resolved') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Effort Score</h1>
          <p className="mt-2 text-gray-600">Phase 224 - Measure and reduce customer effort</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Overall CES</p>
              <p className={`text-3xl font-bold ${getCESColor(statistics.overall_ces)}`}>
                {statistics.overall_ces}/7
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Friction Points</p>
              <p className="text-2xl font-bold text-red-600">{statistics.friction_points}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Resolution Time</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_resolution_time} min</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Improvement Rate</p>
              <p className="text-2xl font-bold text-green-600">+{statistics.improvement_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Effort Scores by Touchpoint</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scores.map((score) => (
              <div key={score.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900">{score.touchpoint}</span>
                  <span className={`text-lg font-bold ${getCESColor(score.ces_score)}`}>
                    {score.ces_score}/7
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${score.ces_score <= 2 ? 'bg-green-500' : score.ces_score <= 4 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${(score.ces_score / 7) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mb-2">
                  <span>{score.responses} responses</span>
                  <span className={score.trend === 'improving' ? 'text-green-600' : score.trend === 'declining' ? 'text-red-600' : 'text-gray-600'}>
                    {score.trend}
                  </span>
                </div>
                {score.improvement_suggestions.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
                    {score.improvement_suggestions.slice(0, 2).map((sug, idx) => (
                      <p key={idx} className="text-xs text-purple-600">• {sug}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">High Friction Points</h2>
            </div>
            <div className="p-6 space-y-4">
              {frictionPoints.map((point) => (
                <div key={point.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900">{point.name}</span>
                      <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{point.category}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-sm ${getStatusBadge(point.resolution_status)}`}>
                      {point.resolution_status}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={`${getEffortBadge(point.effort_level)} px-2 py-0.5 rounded`}>
                      {point.effort_level} effort
                    </span>
                    <span className="text-gray-500">{point.occurrences} occurrences</span>
                    <span className="text-blue-600">Impact: {point.impact}/10</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Journey Effort Analysis</h2>
            </div>
            <div className="p-6 space-y-4">
              {journeys.map((journey) => (
                <div key={journey.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{journey.name}</span>
                    <span className={`text-lg font-bold ${getCESColor(journey.total_effort)}`}>
                      {journey.total_effort}/7
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Steps</p>
                      <p className="font-medium text-gray-900">{journey.steps}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Avg Time</p>
                      <p className="font-medium text-blue-600">{journey.avg_time_minutes}m</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Completion</p>
                      <p className="font-medium text-green-600">{journey.completion_rate}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
